import useApi from "@/services/useApi";
import i18n from "@/i18n";
import {showToast} from '@/helpers/showToast'

// the API answers in English only, so the cabinet shows its own copy
const toast = (moshaToast, type, key) => showToast(moshaToast, type, i18n.global.t(key))

// the API has no error codes, only these fixed descriptions (docs/wiki/calls-api.md);
// the ones a user can act on get their own copy, the rest fall back to the action's error
const apiErrors = {
    'Extension limit for current tariff has been reached': 'telephony.toast.errors.sip_limit',
    'Extension already exists': 'telephony.toast.errors.sip_exists',
    'Extension not found': 'telephony.toast.errors.sip_not_found',
    'Extension is not bound to this number': 'telephony.toast.errors.sip_not_bound',
    'Number not found': 'telephony.toast.errors.number_not_found',
    'Number not found or does not belong to the specified account': 'telephony.toast.errors.number_not_found',
    'Number does not belong to this account': 'telephony.toast.errors.number_not_found',
    'Tariff Expired': 'telephony.toast.errors.tariff_expired',
    'Webhook already exists for this number': 'telephony.toast.errors.webhook_exists',
    'Webhook not found for this number': 'telephony.toast.errors.webhook_not_found',
}

const toastError = (moshaToast, error, fallbackKey) =>
    toast(moshaToast, 'error', apiErrors[error?.response?.data?.description] ?? fallbackKey)

const defaultState = () => {
    return {
        statistics: {
            incoming_calls: 0,
            outgoing_calls: 0,
            total_calls: 0,
            unanswered_outgoing_calls: 0,
            missed_incoming_calls: 0
        },
        callsHistory: [],
        historyMeta: {},
        connectedNumbers: {
            meta: {},
            data: []
        },
        sip: {
            meta: {},
            data: []
        }
    }
}

const telephonyModule = {
    namespaced: true,
    state: defaultState(),
    getters: {
        getCards: (state) => {
            return state.statistics
        },
        getCallsHistory: (state) => {
            return state.callsHistory
        },
        getCallsMeta: (state) => {
            return state.historyMeta
        },
        connectedNumbersList: (state) => {
            return state.connectedNumbers.data
        },
        connectedNumbersMeta: (state) => {
            return state.connectedNumbers.meta
        },
        sips: (state) => {
            return state.sip.data
        },
        sipsMeta: (state) => {
            return state.sip.meta
        }
    },
    mutations: {
        SET_CARDS(state, cards) {
            state.statistics = cards
        },
        SET_HISTORY_DATA(state, {data, append}) {
            const calls = data?.data ?? []

            state.callsHistory = append ? [...state.callsHistory, ...calls] : calls
            state.historyMeta = data?.meta ?? {}
        },
        SET_CONNECTED_NUMBERS_DATA(state, {data, append}) {
            const numbers = data?.numbers ?? []

            state.connectedNumbers.meta = data?.meta ?? {}
            state.connectedNumbers.data = append ? [...state.connectedNumbers.data, ...numbers] : numbers
        },
        SET_SIP_DATA(state, {data, append}) {
            const extensions = data?.extensions ?? []

            state.sip.meta = data?.meta ?? {}
            state.sip.data = append ? [...state.sip.data, ...extensions] : extensions
        },
        UPDATE_NUMBER_EXTENSIONS(state, {number, extensions, delta}) {
            state.connectedNumbers.data = state.connectedNumbers.data.map((el) => {
                if (el.number !== number) return el

                return {
                    ...el,
                    extensions: extensions ?? el.extensions,
                    extensions_count: Math.max(0, Number(el.extensions_count ?? 0) + delta)
                }
            })
        },
        UPDATE_SIP_NUMBERS_COUNT(state, {username, delta}) {
            state.sip.data = state.sip.data.map((el) => {
                if (el.credentials?.username !== username) return el

                return {
                    ...el,
                    // the API may send the count as a string, so coerce before adding
                    connected_numbers_count: Math.max(0, Number(el.connected_numbers_count ?? 0) + delta)
                }
            })
        },
        UPDATE_API_KEY_BY_NUMBER(state, {number, key}) {
            state.connectedNumbers.data = state.connectedNumbers.data.map((el) => {
                if (el.number === number) {
                    el.api_key = key
                }
                return el
            })
        }
    },
    actions: {
        async getStatisticCards({commit}, moshaToast) {
            const {api, headers} = useApi()

            try {
                const response = await api.get('/api/calls/statistics', {headers});
                commit('SET_CARDS', response.data.data)
            } catch (error) {
                toastError(moshaToast, error, 'telephony.toast.load_error')
            }
        },

        async getCallsHistory({commit}, {query, moshaToast, append}) {
            const {api, headers} = useApi()

            try {
                const response = await api.get('/api/calls/history', {headers, params: query})
                commit('SET_HISTORY_DATA', {data: response.data, append})
            } catch (error) {
                toastError(moshaToast, error, 'telephony.toast.load_error')
            }
        },

        async getConnectedNumbers({commit}, {query, moshaToast, append}) {
            const {api, headers} = useApi()
            const q = {...query, messenger: 'viber_voip'}

            try {
                const response = await api.get('/api/numbers/connected/paginate', {headers, params: q})
                commit('SET_CONNECTED_NUMBERS_DATA', {data: response.data.data, append})
            } catch (error) {
                toastError(moshaToast, error, 'telephony.toast.load_error')
            }
        },

        async getSips({commit}, {query, moshaToast, append}) {
            const {api, headers} = useApi()

            try {
                const response = await api.get('/api/extensions', {headers, params: query})
                commit('SET_SIP_DATA', {data: response.data.data, append})
            } catch (error) {
                toastError(moshaToast, error, 'telephony.toast.load_error')
            }
        },

        async getFirstExtension() {
            const {api, headers} = useApi()

            const response = await api.get('/api/extensions', {headers, params: {page: 1}})

            return response.data?.data?.extensions?.[0] ?? null
        },

        async updateApiKey({commit}, {number, moshaToast}) {
            const {api, headers} = useApi()
            const params = {
                messenger: "viber_voip",
                number
            }
            try {
                const response = await api.post('/api/numbers/update-api-key', params, {headers})
                toast(moshaToast, 'success', 'dialog.refresh_api_key.toast_success')
                commit('UPDATE_API_KEY_BY_NUMBER', {number, key: response.data.data.api_key})
            } catch (error) {
                toastError(moshaToast, error, 'dialog.refresh_api_key.toast_error')
            }
        },

        async addWebhook({commit}, {number, webhooks, moshaToast}) {
            const {api, headers} = useApi()
            const params = {
                messenger: "viber_voip",
                number,
                webhooks
            }

            try {
                const response = await api.post('/api/numbers/webhooks', params, {headers})
                toast(moshaToast, 'success', 'dialog.webhooks.success')

                commit('updateDialogParamsInfo', {webhooks}, {root: true})
            } catch (error) {
                toastError(moshaToast, error, 'dialog.webhooks.error')
            }
        },

        async editWebhookByNumber({commit}, {number, webhooks, moshaToast}) {
            const {api, headers} = useApi()
            const params = {
                messenger: "viber_voip",
                number,
                webhooks
            }

            try {
                const response = await api.put(`/api/numbers/webhooks/${number}`, params, {headers})
                toast(moshaToast, 'success', 'dialog.webhooks.success')
                commit('updateDialogParamsInfo', {webhooks}, {root: true})
            } catch (error) {
                toastError(moshaToast, error, 'dialog.webhooks.error')
            }
        },

        async deleteWebhook({commit}, {number, moshaToast}) {
            const {api, headers} = useApi()
            const params = {
                messenger: "viber_voip",
                number,
            }

            try {
                const response = await api.delete(`/api/numbers/webhooks/${number}`, {headers, params})
                toast(moshaToast, 'success', 'dialog.webhooks.delete_success_msg')
                commit('updateDialogParamsInfo', {webhooks: []}, {root: true})
            } catch (error) {
                toastError(moshaToast, error, 'dialog.webhooks.delete_error_msg')
            }
        },

        async toggleExtensionByNumber({commit, rootState}, {number, username, type, moshaToast}) {
            const {api, headers} = useApi()

            const params = {
                number,
                username,
                type,
            }

            try {
                const response = await api.patch(`/api/extensions/bind-unbind`, params, {headers})
                toast(moshaToast, 'success', type === 'bind' ? 'telephony.toast.sip_bound' : 'telephony.toast.sip_unbound')
                let rootExtensions = rootState.dialogParams.info.extensions
                if (type === 'bind') {
                    rootExtensions.push(username)
                } else {
                    rootExtensions = rootState.dialogParams.info.extensions.filter(el => el !== username)
                }
                commit('updateDialogParamsInfo', {extensions: rootExtensions}, {root: true})
                const delta = type === 'bind' ? 1 : -1
                commit('UPDATE_SIP_NUMBERS_COUNT', {username, delta})
                commit('UPDATE_NUMBER_EXTENSIONS', {number, extensions: rootExtensions, delta})
            } catch (error) {
                toastError(moshaToast, error, 'telephony.toast.sip_bind_error')
                throw error?.response?.data
            }
        },

        async createExtension({commit, rootState}, {name, number, moshaToast}) {
            const {api, headers} = useApi()
            const params = {
                name,
                number
            }
            let rootExtensions = rootState.dialogParams.info.extensions

            try {
                const response = await api.post(`/api/extensions`, params, {headers})
                toast(moshaToast, 'success', 'telephony.toast.sip_created')
                const extensions = [...rootExtensions, response.data.data.extension]
                commit('updateDialogParamsInfo', {extensions}, {root: true})
                commit('UPDATE_NUMBER_EXTENSIONS', {number, extensions, delta: 1})
            } catch (error) {
                toastError(moshaToast, error, 'telephony.toast.sip_create_error')
            }
        },
        async deleteExtension({commit, rootState}, {username, number, moshaToast}) {
            const {api, headers} = useApi()
            const rootExtensions = rootState.dialogParams?.info?.extensions ?? []
            const wasBound = rootExtensions.includes(username)

            try {
                const response = await api.delete(`/api/extensions`, {headers, params: {username}})
                toast(moshaToast, 'success', 'telephony.toast.sip_deleted')
                const extensions = rootExtensions.filter((el) => el !== username)
                commit('updateDialogParamsInfo', {extensions}, {root: true})

                if (number) {
                    commit('UPDATE_NUMBER_EXTENSIONS', {
                        number,
                        extensions,
                        delta: wasBound ? -1 : 0
                    })
                }
            } catch (error) {
                toastError(moshaToast, error, 'telephony.toast.sip_delete_error')
                throw error
            }
        },
        async editExtension({commit}, {username, name, password, moshaToast}) {
            const {api, headers} = useApi()
            const params = {
                username,
                name,
                password
            }

            try {
                const response = await api.put(`/api/extensions`, params, {headers})
                toast(moshaToast, 'success', 'telephony.toast.sip_updated')
            } catch (error) {
                toastError(moshaToast, error, 'telephony.toast.sip_update_error')
            }
        }
    }
}

export default telephonyModule
